import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';

import { GetSavannaCurriculumDocument } from '@sandtech-hub-monorepo/ehub-client';

import { CourseAccordion } from './CourseAccordion';
import { InteractiveModuleRow } from './InteractiveModuleRow';
import {
  lockedBeforeFetch,
  SavannaCourseLocation,
  savannaCourseToCourse,
} from '../../data/savannaCurriculum';
import { CurriculumError } from './CurriculumError';
import { CurriculumTimeout } from './CurriculumTimeout';
import { ModuleListSkeleton } from './ModuleListSkeleton';

/*
 * Enrolling only writes eHub's own ProgramEnrollment/ClassEnrollment rows;
 * telling Savanna about the new learner happens later, asynchronously, off
 * a Kinesis event (apps/event-processors' savanna-event-processor). Until
 * that catches up, `savannaCurriculum(id, sandId).flow` genuinely comes back
 * null - indistinguishable at the GraphQL level from a curriculum that
 * really has no content - so a learner opening this course right after
 * enrolling saw a permanently empty list.
 *
 * Polling papers over the race rather than closing it: there is no signal
 * from the backend for "the sync finished", so this keeps asking until
 * either an answer shows up or the cutoff below gives up and renders the
 * empty state exactly as before. A genuinely empty (paced) curriculum pays
 * for the full cutoff once, same as it always would have.
 */
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 21000;

interface Props {
  location: SavannaCourseLocation;
  /** 1-based position in the rendered list, shown in the accordion badge. */
  index: number;
  /** The learner's sandId. Always present here - this tree is enrolled-only. */
  sandId: string;
  defaultOpen?: boolean;
  paidUnlocked: boolean;
  onSubscribe: () => void;
}

/**
 * One course of the interactive tree.
 *
 * Fetched eagerly on first render, every course at once - product wants the
 * whole structure available immediately rather than paid for one course at a
 * time as the learner expands them. Only the first course opens by default;
 * the rest start closed but already hold their content, so opening any of
 * them shows real data with no fetch delay.
 */
export const InteractiveCourseAccordion = ({
  location,
  index,
  sandId,
  defaultOpen = false,
  paidUnlocked,
  onSubscribe,
}: Props) => {
  const [pollingExpired, setPollingExpired] = useState(false);

  const { data, loading, error, stopPolling } = useQuery(
    GetSavannaCurriculumDocument,
    {
      variables: { id: location.curriculumId, sandId },
      fetchPolicy: 'cache-first',
      pollInterval: POLL_INTERVAL_MS,
    }
  );

  const flow = data?.savannaCurriculum?.flow;

  // Apollo's own stop condition: once an answer shows up or the cutoff below
  // gives up, there is nothing left worth re-asking for.
  useEffect(() => {
    if (flow || pollingExpired) {
      stopPolling();
    }
  }, [flow, pollingExpired, stopPolling]);

  useEffect(() => {
    if (flow || pollingExpired) return;

    const timeout = setTimeout(() => setPollingExpired(true), POLL_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [flow, pollingExpired]);

  // While actively retrying, this reads the same as the initial fetch - the
  // skeleton, not the empty state - so a learner mid-sync sees "still
  // loading" rather than a course that looks like it has nothing in it.
  const stillWaiting = !loading && !error && !flow && !pollingExpired;

  // The cutoff was reached and the sync never caught up: this is what used to
  // silently render as an empty course, indistinguishable from one that
  // genuinely has no content.
  const timedOut = pollingExpired && !flow;

  const course = useMemo(() => {
    const mapped = savannaCourseToCourse(location, index, flow, true);

    /*
     * Until the flow arrives there are no tracks to read `locked` from, so
     * the mapping says "not locked" - and a collapsed course would show no
     * lock and no "Subscribe to unlock" to a learner without a subscription,
     * which is exactly the audience the badge is for. Stand in with the
     * product rule for the brief window this fetch is in flight.
     */
    return flow ? mapped : { ...mapped, locked: lockedBeforeFetch(index) };
  }, [location, index, flow]);

  return (
    <CourseAccordion
      course={course}
      defaultOpen={defaultOpen}
      // The default "has modules" test would keep the header disabled for as
      // long as this course's own fetch is in flight - it should open right
      // away and show the loading skeleton, same as the first course does.
      canOpen
      paidUnlocked={paidUnlocked}
      onSubscribe={onSubscribe}
    >
      {(loading || stillWaiting) && <ModuleListSkeleton />}
      {!loading && error && <CurriculumError subject="this course" />}
      {!loading && !stillWaiting && !error && timedOut && (
        <CurriculumTimeout subject={course.title} />
      )}
      {!loading && !stillWaiting && !error && !timedOut && (
        <div className="flex flex-col divide-y divide-border-secondary">
          {course.modules.map((module, moduleIndex) => (
            <InteractiveModuleRow
              key={module.id}
              index={moduleIndex + 1}
              module={module}
              defaultOpen={moduleIndex === 0}
              paidUnlocked={paidUnlocked}
              curriculumId={course.curriculumId}
            />
          ))}
        </div>
      )}
    </CourseAccordion>
  );
};
